/**
 * Die Liste der offenen Posten (Spec 04a, „Reiter Offene Posten") im Bereich
 * *Geld* (`open-items`). Bis Spec 07 trug sie eine zweite, ungefilterte
 * Kurzfassung auf dem inzwischen entfallenen Bildschirm „Heute" — mit dem
 * Kalender ist dieser Bildschirm entfallen (AK-456), „Alle offenen Posten"
 * stehen seither nur noch hier. Bezeichner der Zustände bleiben trotzdem
 * über Props einstellbar, nicht fest verdrahtet — dieselbe Form wie jede
 * andere geteilte Listenkomponente in diesem Baum.
 *
 * Status ausschließlich als 3px breiter linker Rand (`statusBorderClass`),
 * nie flächige Einfärbung; „überfällig" bekommt zusätzlich eine
 * Punktmarkierung, die Rückstandswarnung ein Zeichen mit
 * `title`/`aria-label` — Farbe ist nie das einzige Signal (`design-system`).
 *
 * Beträge kommen ausschließlich als `*_display` vom Server (I9); `due_on`
 * trägt anders als jedes andere Feld dieser Zeile kein eigenes `*_display`
 * — `lib/date.ts` formatiert es als reine Tag.Monat.Jahr-Darstellung, ohne
 * einen Monatsnamen zu bilden.
 *
 * **Schnellaktion `onPay`** (Spec 04b, „Schnellaktion in der Liste offener
 * Posten"). Sie sitzt **neben**, nicht **in** dem
 * `Link`, der die Zeile umschließt: Ein `<button>` in einem `<a>` ist
 * ungültiges HTML und für Tastatur/Screenreader nicht fassbar. Absolut
 * positioniert malt sie über der Zeile, ohne deren Klickziel zu verändern —
 * ein Klick auf die Zeile bleibt Navigation (AK-318 aus 04a bleibt grün).
 */

import { TriangleAlert } from 'lucide-react'

import { ApiError } from '@/lib/api'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { formatDate } from '@/lib/date'
import type { OpenItem, useOpenItems } from '@/features/billing/api'
import { FIELD_CLASS } from '@/components/FormField'
import { statusBorderClass } from '@/features/billing/status'

interface OpenItemsListProps {
  readonly query: ReturnType<typeof useOpenItems>
  readonly listTestId: string
  readonly loadingTestId: string
  readonly errorTestId: string
  readonly retryTestId: string
  readonly emptyTestId: string
  readonly emptyTitle: string
  readonly emptyHint?: string
  readonly rowTestId: (chargeId: string) => string
  readonly overdueTestId: (chargeId: string) => string
  readonly arrearsTestId: (chargeId: string) => string
  /** Eine Folgesektion im selben Scrollbereich darf nicht von der Summe
   * überdeckt werden. */
  readonly stickyTotals?: boolean
  /** „Zahlung erfassen" (`open-item-pay-<charge_id>`), beim Überfahren
   * sichtbar, auf Touchgeräten dauerhaft (Spec 04b). */
  readonly onPay?: (item: OpenItem) => void
  readonly onOpenContract: (contractId: string) => void
}

export function OpenItemsList({
  query,
  listTestId,
  loadingTestId,
  errorTestId,
  retryTestId,
  emptyTestId,
  emptyTitle,
  emptyHint,
  rowTestId,
  overdueTestId,
  arrearsTestId,
  stickyTotals = true,
  onPay,
  onOpenContract,
}: OpenItemsListProps) {
  const accessDenied = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  return (
    <div data-testid={listTestId} className="flex flex-col">
      {query.isPending && <SkeletonRows testId={loadingTestId} rows={5} />}

      {query.isError && (!query.isFetchNextPageError || accessDenied) && (
        <ErrorState
          testId={errorTestId}
          retryTestId={retryTestId}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState testId={emptyTestId} title={emptyTitle} {...(emptyHint !== undefined ? { hint: emptyHint } : {})} />
      )}

      {!accessDenied && query.data !== undefined && query.data.items.length > 0 && (
        <>
          <OpenItemsHeader hasRowAction={onPay !== undefined} />
          <ul>
            {query.data.items.map((item) => (
              <OpenItemRow
                key={item.charge_id}
                item={item}
                rowTestId={rowTestId(item.charge_id)}
                overdueTestId={overdueTestId(item.charge_id)}
                arrearsTestId={arrearsTestId(item.charge_id)}
                {...(onPay !== undefined ? { onPay } : {})}
                onOpenContract={onOpenContract}
              />
            ))}
          </ul>

          <div
            data-testid="open-items-total"
            data-numeric
            className={`border-border bg-surface flex justify-end gap-3 border-t px-(--page-x) py-2 ${stickyTotals ? 'sticky bottom-0' : ''}`}
          >
            <span className="text-label text-muted">{query.data.totals.count} Posten</span>
            <span className="text-body text-text font-medium">{query.data.totals.open_display}</span>
          </div>
        </>
      )}
      {query.isFetchNextPageError && !accessDenied && (
        <ErrorState testId="open-items-more-error" retryTestId="open-items-more-retry" error={query.error} onRetry={() => { void query.fetchNextPage() }} />
      )}
      {query.hasNextPage && !query.isFetchNextPageError && !accessDenied && (
        <button type="button" data-testid="open-items-more" disabled={query.isFetchingNextPage} onClick={() => { void query.fetchNextPage() }} className="border-border text-text border px-3">
          {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}
    </div>
  )
}

/**
 * Die Spaltenbreiten — **eine** Quelle für Kopf und Zeile.
 *
 * Zweimal hingeschriebene Breiten driften beim ersten Eingriff auseinander,
 * und ein Spaltenkopf, der einen Finger neben seiner Spalte steht, ist
 * schlimmer als gar keiner. Nur ab 768px wirksam: Darunter ist die Zeile
 * `flex-col` — eine Karte, die keine Spalten hat und deshalb auch keinen Kopf
 * bekommt.
 */
const COLUMN = {
  tenant: 'md:flex-1',
  subject: 'md:flex-1',
  /* Breit genug für „September 2026 · Nebenkosten", den längsten Fall
   * (gefunden bei den Aufnahmen zu Spec 04a). */
  period: 'md:w-48',
  due: 'md:w-32',
  charged: 'md:w-24',
  open: 'md:w-24',
} as const

/**
 * Der Spaltenkopf (Befund B4, `tasks/todo-web-schale-b.md`).
 *
 * Ohne ihn standen zwei Beträge nebeneinander, ohne dass irgendwo stand,
 * welcher welcher ist — die häufigste Frage an dieser Tabelle. „Soll" ist der
 * geforderte Betrag (`charged_display`; *Charge* heißt an der Oberfläche
 * **Sollstellung**, `design-system`), „Offen" der Rest.
 *
 * `sticky`, weil die Liste lang wird und ein Kopf, der beim Scrollen
 * verschwindet, nach der zehnten Zeile nichts mehr erklärt. `pl-3` spiegelt
 * den Innenabstand der Zeile; `border-l-status border-l-transparent` nimmt
 * denselben 3px-Rand auf, den `statusBorderClass` an jede Zeile hängt — ohne
 * ihn stünde der Kopf um genau diese drei Pixel versetzt.
 */
function OpenItemsHeader({ hasRowAction }: { hasRowAction: boolean }) {
  return (
    <div
      // **Kein `data-testid`.** Der Skill verlangt eins für jeden
      // *interaktiven* Bereich; ein Spaltenkopf ist reine Beschriftung. Und
      // er hätte kein iOS-Gegenstück: Eine Tabelle mit Spaltenköpfen gibt es
      // nur am Schreibtisch, auf dem Telefon stehen dieselben Daten als
      // Karten. Ein Bezeichner hier hätte `check-invariants` verlangt, ihn in
      // die Liste der web-eigenen Ausnahmen zu schreiben — für etwas, das gar
      // kein Testziel ist. Ob der Kopf steht, zeigt die Aufnahme.
      // Spiegelt den **Aufbau** der Zeile, nicht nur ihre Breiten: außen ein
      // Flex-Container ohne Innenabstand, darin der Spaltenteil mit `flex-1`
      // und daneben der Platz der Schnellaktion. Ein Kopf, der stattdessen
      // alle Spalten und den Platzhalter in *einen* Container mit `gap-3`
      // legt, steht 12px daneben — das Gap zwischen Zeile und Aktion gibt es
      // in der `<li>` nicht. Gemessen, nicht geschätzt.
      className="border-border bg-bg text-label text-muted border-l-status sticky top-0 z-10 hidden border-b border-l-transparent md:flex"
    >
      <div className="flex flex-1 items-center gap-3 py-1 pr-(--page-x) pl-3">
        <span className={COLUMN.tenant}>Mieter</span>
        <span className={COLUMN.subject}>Objekt</span>
        <span className={COLUMN.period}>Zeitraum</span>
        <span className={COLUMN.due}>Fällig</span>
        <span data-numeric className={COLUMN.charged}>
          Soll
        </span>
        <span data-numeric className={COLUMN.open}>
          Offen
        </span>
      </div>
      {/* Die Zeile reserviert rechts den Platz ihrer Schnellaktion, damit das
       * Einblenden beim Überfahren nichts verschiebt. Derselbe Platz muss
       * hier stehen, sonst sind Kopf und Werte gegeneinander versetzt.
       *
       * Ein unsichtbarer Klon statt einer festen Breite: Er misst sich am
       * selben Text und bleibt richtig, wenn die Beschriftung sich ändert.
       * `aria-hidden`, weil er keine Information trägt, nur Platz. */}
      {hasRowAction && (
        <span aria-hidden="true" className={`invisible mx-2 my-1 shrink-0 px-3 ${FIELD_CLASS}`}>
          Zahlung erfassen
        </span>
      )}
    </div>
  )
}

interface OpenItemRowProps {
  readonly item: OpenItem
  readonly rowTestId: string
  readonly overdueTestId: string
  readonly arrearsTestId: string
  readonly onPay?: (item: OpenItem) => void
  readonly onOpenContract: (contractId: string) => void
}

function OpenItemRow({ item, rowTestId, overdueTestId, arrearsTestId, onPay, onOpenContract }: OpenItemRowProps) {
  return (
    <li
      className={`group flex flex-col border-b-border border-b last:border-b-0 md:flex-row md:items-stretch ${statusBorderClass(item.status.value)}`}
    >
      <button
        type="button"
        data-testid={rowTestId}
        onClick={() => {
          onOpenContract(item.contract_id)
        }}
        className="hover:bg-bg flex min-h-(--row-h) flex-1 flex-col gap-1 py-2 pr-(--page-x) pl-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) md:min-h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-1"
      >
        <span className={`flex min-w-0 items-center gap-1.5 ${COLUMN.tenant}`}>
          <span className="text-body text-text truncate font-medium">{item.tenant.label}</span>
          {item.arrears_warning && (
            <span
              data-testid={arrearsTestId}
              role="img"
              aria-label="Rückstand über zwei Monatsmieten"
              title="Rückstand über zwei Monatsmieten"
              className="inline-flex shrink-0"
            >
              <TriangleAlert aria-hidden="true" className="text-status-overdue size-3.5" />
            </span>
          )}
        </span>
        <span className={`text-label text-muted truncate ${COLUMN.subject}`}>{item.subject.label}</span>
        <span className={`text-label text-muted ${COLUMN.period}`}>
          {item.period_display}
          {item.kind.value !== 'rent' ? ` · ${item.kind.label}` : ''}
        </span>
        <span className={`text-label text-muted flex items-center gap-1.5 whitespace-nowrap ${COLUMN.due}`}>
          {item.overdue && (
            <span
              data-testid={overdueTestId}
              role="img"
              aria-label="Überfällig"
              className="bg-status-overdue size-1.5 shrink-0 rounded-full"
            />
          )}
          fällig {formatDate(item.due_on)}
        </span>
        {/* Unter dem `md:`-Umbruch fällt die Tabelle zu Karten zusammen — dort
         * standen die beiden Beträge ohne Beschriftung untereinander, also
         * genau der Befund B4 („zwei Beträge nebeneinander, ohne dass
         * irgendwo steht, welcher welcher ist"), nur am schmalen Ende. Er
         * blieb stehen, weil es bis 2026-08-28 keine 380-px-Aufnahme gab.
         *
         * `md:contents` löst den Wrapper am Zeiger wieder auf: Dort bleibt das
         * Betragselement direktes Flex-Kind und behält seine Spaltenbreite,
         * die Beschriftung übernimmt der Spaltenkopf. Das Label steht
         * **außerhalb** von `[data-numeric]` — die Journey 04a liest dieses
         * Element mit `toHaveText` exakt gegen `*_display`, und ein Label
         * darin hätte sie gebrochen (Regel 9). */}
        <span className="flex items-baseline justify-between gap-2 md:contents">
          <span className="text-label text-muted md:hidden">Soll</span>
          <span data-numeric className={`text-body text-text ${COLUMN.charged}`}>
            {item.charged_display}
          </span>
        </span>
        <span className="flex items-baseline justify-between gap-2 md:contents">
          <span className="text-label text-muted md:hidden">Offen</span>
          <span data-numeric className={`text-body text-text font-medium ${COLUMN.open}`}>
            {item.open_display}
          </span>
        </span>
      </button>

      {/* Schnellaktion als eigenständiges Geschwisterelement, nicht in `Link`
       * verschachtelt (ungültiges HTML, `<button>` in `<a>`). Sichtbarkeit
       * über `opacity`, nicht `display` — der Platz bleibt reserviert, damit
       * das Einblenden beim Überfahren keine Zeile verschiebt. Auf
       * Touchgeräten (unter dem `md:`-Umbruch) bleibt sie ohne die
       * `opacity-0`-Vorgabe dauerhaft sichtbar (Spec 04b, „Schmale
       * Ansicht").
       *
       * **Sekundäre Form, nicht die Akzentfläche** (`FIELD_CLASS`, wie
       * `payment-create-cancel`): Der Akzent markiert die primäre Aktion
       * einer Ansicht (`design-system`, „Farbe"/„Verboten") — bei 380 px
       * steht diese Zeilenaktion dauerhaft und mehrfach untereinander, eine
       * Akzentfüllung hätte fünf „primäre" Aktionen auf einem Bildschirm
       * ergeben (Gestaltungswarnung W1, `reports/12-design-reviewer.md`). */}
      {onPay !== undefined && (
        <button
          type="button"
          data-testid={`open-item-pay-${item.charge_id}`}
          onClick={() => {
            onPay(item)
          }}
          // `md:my-0`: Am Zeiger bestimmte bislang diese Zeilenaktion die
          // Zeilenhöhe — 36px Ziel plus 2×6px Margin ergaben 48px, und der
          // Knopf ist dort im Ruhezustand `opacity-0`. Die Zeile war also um
          // ein Drittel höher wegen eines Knopfes, den man erst beim
          // Überfahren sieht. Er füllt jetzt die Zeile, statt sie zu dehnen;
          // am Telefon bleibt es bei `mb-2` und der vollen Touchgröße.
          className={`mx-3 mb-2 flex min-h-(--tap-min) shrink-0 items-center justify-center md:mx-2 md:my-0 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100 ${FIELD_CLASS}`}
        >
          Zahlung erfassen
        </button>
      )}
    </li>
  )
}
